import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Gem, LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { buttonVariants } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import PremiumModal from '../../../../components/PremiumModal'
import { ImageViewerItem } from '../../../../components/ImageViewerDialog'
import IntegrationIcon, {
  IntegrationRef,
  integrationKeyOf,
} from './integrations'
import ScreenshotShowcase from './ScreenshotShowcase'
import Reveal from './Reveal'
import { GameVersionCapability } from 'app/frontend/types/custom/GameVersion'

export interface DetailLink {
  label: string
  to: string
  external?: boolean
}

export interface DetailSectionItem {
  id: string
  icon: LucideIcon
  heading: string
  description: string
  bullets: React.ReactNode[]
  integrations: IntegrationRef[]
  screenshots: ImageViewerItem[]
  links?: DetailLink[]
  requires?: GameVersionCapability
  excludes?: GameVersionCapability
  premium?: boolean
}

interface Props {
  section: DetailSectionItem
  reversed?: boolean
  tinted?: boolean
}

const FeatureDetailSection = ({ section, reversed, tinted }: Props) => {
  const { t } = useTranslation()
  const {
    id,
    icon: Icon,
    heading,
    description,
    bullets,
    integrations,
    screenshots,
    links,
    premium,
  } = section
  const hasScreenshots = screenshots.length > 0

  return (
    <section className={cn('w-full', tinted ? 'bg-muted/40' : '')}>
      <Reveal
        id={`section-${id}`}
        className={cn(
          'root-width scroll-mt-24 grid grid-cols-1 items-center gap-8 py-16 lg:gap-12 lg:py-20',
          hasScreenshots && 'lg:grid-cols-2',
        )}
      >
        <div
          className={cn('flex flex-col gap-4', reversed ? 'lg:order-2' : '')}
        >
          <h2 className='text-2xl font-medium flex items-center gap-4 mb-0'>
            <div className='flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary'>
              <Icon className='h-5 w-5' />
            </div>
            <span className='flex items-center gap-3'>
              {heading}
              {premium ? (
                <Tooltip>
                  <PremiumModal>
                    <TooltipTrigger asChild>
                      <Gem className='h-4 w-4 text-subscription-gold mt-1 cursor-pointer' />
                    </TooltipTrigger>
                  </PremiumModal>
                  <TooltipContent>
                    {t('homepage.premium-feature')}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </span>
          </h2>
          <p className='text-muted-foreground'>{description}</p>

          <ul className='flex flex-col gap-2'>
            {bullets.map((bullet, i) => (
              <li key={i} className='flex items-start gap-2'>
                <Check className='mt-0.5 h-4 w-4 shrink-0 text-primary' />
                <span className='text-sm'>{bullet}</span>
              </li>
            ))}
          </ul>

          {integrations.length > 0 ? (
            <div className='flex flex-wrap gap-2'>
              {integrations.map((integration) => (
                <IntegrationIcon
                  key={integrationKeyOf(integration)}
                  integration={integration}
                />
              ))}
            </div>
          ) : null}

          {links && links.length > 0 ? (
            <div className='mt-2 flex flex-wrap gap-3'>
              {links.map((link) =>
                link.external ? (
                  <a
                    key={link.to}
                    href={link.to}
                    target='_blank'
                    rel='noreferrer'
                    className={buttonVariants({
                      variant: 'outline',
                      size: 'sm',
                    })}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={buttonVariants({
                      variant: 'outline',
                      size: 'sm',
                    })}
                  >
                    {link.label}
                  </Link>
                ),
              )}
            </div>
          ) : null}
        </div>

        {hasScreenshots ? (
          <div className={cn(reversed ? 'lg:order-1' : '')}>
            <ScreenshotShowcase screenshots={screenshots} />
          </div>
        ) : null}
      </Reveal>
    </section>
  )
}

export default FeatureDetailSection
